import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip as MuiTooltip,
  Pagination,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowBack, AutoAwesome, ExpandMore, FileDownloadOutlined } from "@mui/icons-material";
import { useMutation } from "react-query";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { useSurveyAnalytics, useSurveyDetails, useSurveyResponses } from "../../hooks/useSurveys";
import { exportSurveyResponsesCsv, getDashboardMetricInsights } from "../../api/client";
import MetricAiInsightsDialog from "../../components/MetricAiInsightsDialog";
import SurveyPeriodFilterBar from "./SurveyPeriodFilterBar";
import { buildSurveyPeriodFilters, getDefaultSurveyPeriod, resolveSurveyPeriodFilters } from "./surveyPeriod";

const SESSION_STATUS_LABELS = {
  completed: "Concluída",
  started: "Iniciada",
};

const NPS_COLORS = ["#2e7d32", "#ed6c02", "#d32f2f"];

const formatPercent = (value) => (value == null ? "—" : `${Number(value).toFixed(1)}%`);
const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return "—";
  const total = Math.max(0, Math.round(Number(seconds)));
  if (total < 60) return `${total} s`;
  if (total < 3600) return `${Math.max(1, Math.round(total / 60))} min`;
  return `${Math.floor(total / 3600)} h ${Math.round((total % 3600) / 60)} min`;
};
const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("pt-BR");
};

const toMs = (value) => {
  if (!value) return null;
  const d = new Date(value);
  const ms = d.getTime();
  return Number.isNaN(ms) ? null : ms;
};

const getSessionDurationSeconds = (session) => {
  const startedMs = toMs(session?.startedAt);
  const completedMs = toMs(session?.completedAt);
  if (startedMs != null && completedMs != null && completedMs >= startedMs) {
    return Math.max(0, Math.round((completedMs - startedMs) / 1000));
  }
  const fallback = session?.timeToCompleteSeconds ?? session?.timeToComplete ?? null;
  const n = fallback == null ? null : Number(fallback);
  return n == null || Number.isNaN(n) ? null : n;
};

const getMetricTypeLabel = (metricType, mode = "score") => {
  if (mode === "distribution") return "Resposta principal";
  const key = String(metricType || "").toLowerCase();
  if (key === "nps") return "NPS";
  if (key === "csat") return "CSAT";
  if (key === "ces") return "CES";
  return "Indicador";
};

const getPrimaryObjectiveText = (metricType, mode = "score") => {
  if (mode === "distribution") {
    return "Resumo da resposta mais frequente na pergunta principal desta pesquisa.";
  }
  const key = String(metricType || "").toLowerCase();
  if (key === "nps") return "Mede lealdade e probabilidade de recomendação (escala 0–10).";
  if (key === "csat") return "Mede satisfação com atendimento ou serviço em escala numérica.";
  if (key === "ces") return "Mede o esforço do cliente para resolver uma necessidade.";
  return "Acompanhe o indicador principal configurado nesta pesquisa.";
};

const toNumericLabel = (option) => {
  const candidates = [option?.value, option?.label, option?.averageScore];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const value = Number(candidate);
    if (!Number.isNaN(value)) return String(value);
  }
  return null;
};

const getNpsBand = (value, sampleSize = 0) => {
  if (!sampleSize || sampleSize <= 0) return { label: "Sem dados suficientes", color: "default" };
  if (value == null) return { label: "Sem pontuação calculada", color: "default" };
  if (value < 0) return { label: "Zona crítica", color: "error" };
  if (value < 30) return { label: "Zona de atenção", color: "warning" };
  if (value < 60) return { label: "Zona de melhoria", color: "info" };
  return { label: "Zona de excelência", color: "success" };
};

const getQuestionTypeLabel = (type) => {
  const map = {
    nps: "NPS (0–10)",
    rating_scale: "Escala numérica",
    emoji_scale: "Escala emoji",
    single_choice: "Escolha única",
    multiple_choice: "Múltipla escolha",
    yes_no: "Sim / Não",
    short_text: "Texto curto",
    long_text: "Texto longo",
    info: "Conteúdo informativo",
  };
  return map[String(type || "").toLowerCase()] || "Pergunta";
};

const formatOriginLabel = (label) => {
  const raw = String(label || "").trim();
  if (!raw) return "—";
  let cleaned = raw.replace(/^(\d+)\s*-\s*\1\s*-\s*/i, "$1 - ");
  const nested = cleaned.match(/^(\d+)\s*-\s*(\d+)\s*-\s*(.+)$/);
  if (nested && nested[1] !== nested[2]) {
    cleaned = nested[3].trim();
  } else {
    const prefixed = cleaned.match(/^(\d+)\s*-\s*(.+)$/);
    if (prefixed) cleaned = prefixed[2].trim();
  }
  return cleaned || raw;
};

const getSessionSourceLabel = (session) =>
  formatOriginLabel(session?.sourceLabel || session?.sourceChannel || "");

const repairDisplayText = (value) => {
  if (value == null) return "";
  const str = String(value);
  if (!/[ÃÂâ�ðŸ]/.test(str)) return str;
  try {
    const bytes = new Uint8Array([...str].map((char) => char.charCodeAt(0) & 0xff));
    const repaired = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    return repaired.includes("�") ? str : repaired;
  } catch {
    return str;
  }
};

const getQuestionMenuLabel = (question) => {
  const title = repairDisplayText(question?.title || "Sem título");
  const typeLabel = getQuestionTypeLabel(question?.type);
  const answerCount = (question?.options || []).reduce((sum, opt) => sum + Number(opt.count || 0), 0);
  return `${title} · ${typeLabel} (${answerCount})`;
};

const formatResponseCount = (count) => {
  const n = Number(count) || 0;
  if (n === 0) return "Nenhuma resposta";
  if (n === 1) return "1 resposta";
  return `${n} respostas`;
};

const normalizeComparableText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isSurveyLikeQuestionTitle = (text, surveyTitle) => {
  const normalized = normalizeComparableText(text);
  const surveyNorm = normalizeComparableText(surveyTitle);
  if (!normalized) return true;
  if (surveyNorm && normalized === surveyNorm) return true;
  if (surveyNorm && normalized.includes(surveyNorm)) return true;
  return false;
};

const formatMetricValue = (scoreSummary) => {
  const mode = scoreSummary?.mode || "score";
  const metricType = String(scoreSummary?.metricType || "").toLowerCase();
  const value = scoreSummary?.mainValue;

  if (mode === "distribution") {
    if (scoreSummary?.mainLabel && value != null) {
      return `${repairDisplayText(scoreSummary.mainLabel)} · ${formatPercent(value)}`;
    }
    if (scoreSummary?.mainLabel) return repairDisplayText(scoreSummary.mainLabel);
    return "—";
  }

  if (value == null) return "—";
  if (metricType === "nps") return String(value);
  if (metricType === "csat" || metricType === "ces") return formatPercent(value);
  return String(value);
};

const getMetricHelper = (scoreSummary) => {
  const mode = scoreSummary?.mode || "score";
  const sampleSize = Number(scoreSummary?.sampleSize || 0);
  if (sampleSize > 0 && mode === "distribution") {
    return `Com base em ${sampleSize} resposta${sampleSize === 1 ? "" : "s"} na pergunta principal`;
  }
  if (sampleSize > 0) {
    return `Com base em ${sampleSize} resposta${sampleSize === 1 ? "" : "s"} pontuadas`;
  }
  return "A pergunta principal ainda não recebeu respostas neste recorte.";
};

const SESSIONS_PAGE_SIZE = 20;

const sessionSummaryGridSx = {
  display: { xs: "none", lg: "grid" },
  gridTemplateColumns: "148px minmax(120px, 1.1fr) 88px minmax(100px, 0.9fr) 92px 72px 96px",
  gap: 2,
  alignItems: "center",
  width: "100%",
  pr: 1,
};

const formatProtocolLabel = (leadId) => {
  if (leadId == null || leadId === "") return "";
  const n = Number(leadId);
  if (!Number.isFinite(n) || n <= 0) return "";
  return String(n);
};

const StatCard = ({ label, value, helper }) => (
  <Card
    variant="outlined"
    sx={{
      height: "100%",
      borderRadius: 2,
      borderColor: "divider",
      boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
      bgcolor: "background.paper",
    }}
  >
    <CardContent sx={{ p: 2.25 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1, lineHeight: 1.2, fontWeight: 700 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {helper}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

export default function SurveyAnalyticsPage() {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const surveyId = Number(id);
  const defaultPeriod = React.useMemo(() => getDefaultSurveyPeriod(), []);
  const [startDate, setStartDate] = useState(defaultPeriod.startDate);
  const [endDate, setEndDate] = useState(defaultPeriod.endDate);
  const debouncedStartDate = useDebouncedValue(startDate, 400);
  const debouncedEndDate = useDebouncedValue(endDate, 400);
  const [sourceLabel, setSourceLabel] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportNotice, setExportNotice] = useState(null);
  const [sessionsPage, setSessionsPage] = useState(1);
  const stablePeriodFiltersRef = React.useRef(buildSurveyPeriodFilters(defaultPeriod.startDate, defaultPeriod.endDate));
  const analyticsFilters = useMemo(() => {
    const resolved = resolveSurveyPeriodFilters(
      debouncedStartDate,
      debouncedEndDate,
      stablePeriodFiltersRef.current
    );
    if (resolved) {
      stablePeriodFiltersRef.current = resolved;
    }
    return {
      ...stablePeriodFiltersRef.current,
      sourceLabel: sourceLabel || undefined,
    };
  }, [debouncedStartDate, debouncedEndDate, sourceLabel]);
  const { data: survey, isLoading: loadingSurvey, error: surveyError } = useSurveyDetails(surveyId, true);
  const {
    data: analytics,
    isLoading: loadingAnalytics,
    isFetching: fetchingAnalytics,
    error: analyticsError,
  } = useSurveyAnalytics(surveyId, true, analyticsFilters);
  const {
    data: sessionsData,
    isLoading: loadingSessions,
    isFetching: fetchingSessions,
  } = useSurveyResponses(surveyId, sessionsPage, SESSIONS_PAGE_SIZE, "", true, analyticsFilters);

  React.useEffect(() => {
    setSessionsPage(1);
  }, [debouncedStartDate, debouncedEndDate, sourceLabel]);
  const [selectedQuestionId, setSelectedQuestionId] = useState("");
  const [metricDialogOpen, setMetricDialogOpen] = useState(false);
  const [metricDialogMetric, setMetricDialogMetric] = useState("");
  const [metricDialogName, setMetricDialogName] = useState("");
  const [metricDialogLabel, setMetricDialogLabel] = useState("");
  const [metricDialogHuman, setMetricDialogHuman] = useState("");
  const [metricDialogData, setMetricDialogData] = useState(null);
  const [metricDialogError, setMetricDialogError] = useState(null);
  const [metricDialogBasePayload, setMetricDialogBasePayload] = useState(null);

  const questions = analytics?.questions || [];
  React.useEffect(() => {
    if (!questions.length) return;
    if (selectedQuestionId && questions.some((q) => String(q.questionId) === String(selectedQuestionId))) {
      return;
    }
    const primary =
      questions.find((q) => q.isPrimaryMetric) ||
      questions.slice().sort((a, b) => {
        const aCount = (a.options || []).reduce((sum, opt) => sum + Number(opt.count || 0), 0);
        const bCount = (b.options || []).reduce((sum, opt) => sum + Number(opt.count || 0), 0);
        return bCount - aCount;
      })[0];
    if (primary?.questionId) setSelectedQuestionId(String(primary.questionId));
  }, [questions, selectedQuestionId]);
  const selectedQuestion =
    questions.find((question) => String(question.questionId) === String(selectedQuestionId)) ||
    questions[0] ||
    null;

  const chartData = useMemo(
    () =>
      (selectedQuestion?.options || []).map((option) => ({
        name: (() => {
          const isNumericScale =
            selectedQuestion?.type === "nps" ||
            selectedQuestion?.type === "rating_scale" ||
            selectedQuestion?.type === "emoji_scale";
          if (!isNumericScale) return option.label || option.value || "Opção";
          const numericLabel = toNumericLabel(option);
          return `Nota ${numericLabel || "?"}`;
        })(),
        respostas: Number(option.count || 0),
      })),
    [selectedQuestion]
  );

  const executive = analytics?.executive || {};
  const scoreSummary = executive.scoreSummary || {};
  const metricMode = scoreSummary.mode || "score";
  const metricType = scoreSummary.metricType || survey?.metricType || "custom";
  const metricTypeLabel = getMetricTypeLabel(metricType, metricMode);
  const primaryQuestion = analytics?.executive?.primaryQuestion || null;
  const dataQuality = analytics?.executive?.dataQuality || {};
  const sessionsWithoutAnswers = Number(dataQuality.sessionsWithoutAnswers || 0);
  const metricDisplayValue = formatMetricValue(scoreSummary);
  const metricHelper = getMetricHelper(scoreSummary);
  const sourceOptions = analytics?.filterOptions?.sources || [];
  const hasOpenTextQuestions = questions.some((q) => ["short_text", "long_text"].includes(String(q.type)));
  const npsBand = getNpsBand(scoreSummary.mainValue, scoreSummary.sampleSize);
  const surveyTitleNormalized = String(survey?.title || survey?.titulo || "");
  const questionTitleById = useMemo(() => {
    const map = new Map();
    for (const question of questions) {
      if (question?.questionId) map.set(String(question.questionId), question.title || "");
    }
    return map;
  }, [questions]);
  const primaryQuestionDisplayTitle = useMemo(() => {
    if (!primaryQuestion?.title) return null;
    const title = repairDisplayText(primaryQuestion.title);
    if (isSurveyLikeQuestionTitle(title, surveyTitleNormalized)) {
      return primaryQuestion.position
        ? `Pergunta ${primaryQuestion.position} · ${getQuestionTypeLabel(primaryQuestion.type)}`
        : `${getQuestionTypeLabel(primaryQuestion.type)} · pergunta principal`;
    }
    return title;
  }, [primaryQuestion, surveyTitleNormalized]);
  const npsBreakdown = useMemo(() => {
    if (metricMode !== "score" || metricType !== "nps") return [];
    return [
      { name: "Promotores", value: Number(scoreSummary.promoters || 0) },
      { name: "Neutros", value: Number(scoreSummary.passives || 0) },
      { name: "Detratores", value: Number(scoreSummary.detractors || 0) },
    ].filter((item) => item.value > 0);
  }, [metricType, scoreSummary]);
  const trend = analytics?.trend || [];
  const hasTrend = trend.some(
    (row) => Number(row.startedCount || 0) > 0 || Number(row.completedCount || 0) > 0
  );
  const totalOpenTexts = (analytics?.openTextResponses || []).length;
  const selectedQuestionOpenTexts = useMemo(() => {
    if (!selectedQuestion?.questionId) return [];
    return (analytics?.openTextResponses || []).filter(
      (item) => String(item.questionId) === String(selectedQuestion.questionId)
    );
  }, [analytics?.openTextResponses, selectedQuestion?.questionId]);
  const completionRate = executive.completionRate ?? null;
  const startedCount = Number(executive.startedCount || 0);
  const completedCount = Number(executive.completedCount || 0);
  const dropoffCount = Math.max(0, startedCount - completedCount);
  const sessions = sessionsData?.sessions || [];
  const sessionsPagination = sessionsData?.pagination || { total: 0, page: 1, totalPages: 1 };
  const sessionsTotal = Number(analytics?.sessionsTotal ?? sessionsPagination.total ?? startedCount);
  const avgCompletionSeconds = executive.averageCompletionSeconds ?? null;

  const topOption = useMemo(() => {
    if (!chartData.length) return null;
    return chartData.slice().sort((a, b) => b.respostas - a.respostas)[0];
  }, [chartData]);
  const formatAnswer = (answer) => {
    if (!answer) return null;
    if (answer.optionLabel) return repairDisplayText(answer.optionLabel);
    if (answer.answerText) return repairDisplayText(answer.answerText);
    if (answer.answerNumber != null) return `Nota ${answer.answerNumber}`;
    if (answer.optionValue && !/^\d+$/.test(String(answer.optionValue))) {
      return repairDisplayText(String(answer.optionValue));
    }
    if (answer.answerScore != null) return String(answer.answerScore);
    return null;
  };

  const getAnswerQuestionLabel = (answer) => {
    const candidates = [
      questionTitleById.get(String(answer?.questionId)),
      answer?.questionTitle,
      answer?.questionHint,
    ];
    for (const candidate of candidates) {
      const title = repairDisplayText(candidate || "");
      if (title && !isSurveyLikeQuestionTitle(title, surveyTitleNormalized)) return title;
    }
    const pos = answer?.questionPosition;
    const typeLabel = getQuestionTypeLabel(answer?.questionType);
    if (pos) return typeLabel ? `Pergunta ${pos} · ${typeLabel}` : `Pergunta ${pos}`;
    return typeLabel || "Pergunta";
  };

  const getAnswerQuestionHint = (answer) => {
    const hint = repairDisplayText(answer?.questionHint || "");
    if (!hint || isSurveyLikeQuestionTitle(hint, surveyTitleNormalized)) return null;
    const label = getAnswerQuestionLabel(answer);
    if (normalizeComparableText(hint) === normalizeComparableText(label)) return null;
    return hint;
  };

  const isInitialLoading = loadingSurvey || (loadingAnalytics && !analytics);
  const isRefreshingAnalytics = fetchingAnalytics && Boolean(analytics);
  const errorMessage = surveyError?.message || analyticsError?.message;

  const metricInsightsMutation = useMutation(
    async (cfg) =>
      getDashboardMetricInsights("surveys", {
        metric: cfg.metric,
        metricName: cfg.metricName,
        metricLabel: cfg.metricLabel,
        metricDefinition: cfg.metricDefinition,
        metricValue: cfg.metricValue,
        metricHuman: cfg.metricHuman,
        period: {
          start: survey?.createdAt || survey?.data_cadastro || null,
          end: new Date().toISOString().slice(0, 10),
        },
        summaryContext: {
          surveyId,
          surveyTitle: survey?.title || survey?.titulo || "",
          scoreSummary,
          executive,
        },
      }),
    {
      onSuccess: (data) => {
        setMetricDialogData(data || null);
        setMetricDialogError(null);
      },
      onError: (err) => {
        setMetricDialogError(err?.message || "Não foi possível gerar a explicação da IA.");
      },
    }
  );

  const openAi = (cfg) => {
    setMetricDialogMetric(cfg.metric || "");
    setMetricDialogName(cfg.metricName || "");
    setMetricDialogLabel(cfg.metricLabel || "");
    setMetricDialogHuman(cfg.metricHuman || "");
    setMetricDialogBasePayload(cfg || null);
    setMetricDialogData(null);
    setMetricDialogError(null);
    setMetricDialogOpen(true);
    metricInsightsMutation.mutate(cfg);
  };

  const AiBtn = ({ cfg, title = "Explicar com IA" }) => (
    <MuiTooltip title={title}>
      <IconButton size="small" onClick={() => openAi(cfg)} aria-label={title}>
        <AutoAwesome fontSize="inherit" />
      </IconButton>
    </MuiTooltip>
  );

  const sectionCardSx = {
    borderRadius: 2,
    borderColor: "divider",
    boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
    bgcolor: "background.paper",
  };

  const sectionTitleSx = {
    fontWeight: 700,
    letterSpacing: "-0.01em",
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2.5}>
        <Card variant="outlined" sx={sectionCardSx}>
          <CardContent sx={{ p: 2.25 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={1.5}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                  Resultado da pesquisa
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {survey?.title || survey?.titulo || "Pesquisa"}
                </Typography>
              </Box>
              <Button startIcon={<ArrowBack />} onClick={() => navigate("/surveys")} variant="outlined">
                Voltar para pesquisas
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        {isInitialLoading ? <Alert severity="info">Carregando dados de analytics...</Alert> : null}
        {isRefreshingAnalytics ? (
          <Alert severity="info">Atualizando dados do período selecionado...</Alert>
        ) : null}

        {!loadingSurvey ? (
          <Card variant="outlined" sx={sectionCardSx}>
            <CardContent sx={{ p: 2.25 }}>
              <SurveyPeriodFilterBar
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                extraActions={
                  analytics ? (
                    <>
                      {sourceOptions.length ? (
                        <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 240 } }}>
                          <InputLabel>Canal</InputLabel>
                          <Select
                            label="Canal"
                            value={sourceLabel}
                            onChange={(event) => setSourceLabel(event.target.value)}
                          >
                            <MenuItem value="">Todos os canais</MenuItem>
                            {sourceOptions.map((source) => (
                              <MenuItem key={source.value} value={source.value}>
                                {source.label} ({source.sessionCount})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : null}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSourceLabel("")}
                        disabled={!sourceLabel}
                      >
                        Limpar canal
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<FileDownloadOutlined />}
                        disabled={exporting}
                        onClick={async () => {
                          setExporting(true);
                          setExportNotice(null);
                          try {
                            const result = await exportSurveyResponsesCsv(
                              surveyId,
                              analyticsFilters,
                              `${survey?.slug || `survey-${surveyId}`}-respostas.csv`
                            );
                            if (result?.truncated) {
                              setExportNotice(
                                `Exportação limitada a ${result.rowCount.toLocaleString("pt-BR")} de ${result.totalRows.toLocaleString("pt-BR")} linhas. Ajuste os filtros ou exporte em partes.`
                              );
                            }
                          } finally {
                            setExporting(false);
                          }
                        }}
                      >
                        {exporting ? "Exportando..." : "Exportar respostas"}
                      </Button>
                    </>
                  ) : null
                }
              />
            </CardContent>
          </Card>
        ) : null}

        {!isInitialLoading && analytics && !startedCount && !questions.length ? (
          <Alert severity="warning">
            Ainda não há respostas neste recorte. Ajuste o período ou aguarde novas participações.
          </Alert>
        ) : null}

        {!isInitialLoading && analytics && startedCount > 0 && Number(scoreSummary.sampleSize || 0) === 0 ? (
          <Alert severity="warning">
            Há {startedCount} participação{startedCount === 1 ? "" : "ões"} registrada
            {startedCount === 1 ? "" : "s"}, mas a pergunta principal ainda não recebeu respostas neste
            período.
          </Alert>
        ) : null}

        {!isInitialLoading && analytics && sessionsWithoutAnswers > 0 ? (
          <Alert severity="info">
            {sessionsWithoutAnswers} sessão{sessionsWithoutAnswers === 1 ? "" : "ões"} concluída
            {sessionsWithoutAnswers === 1 ? "" : "s"} sem respostas registradas.
          </Alert>
        ) : null}

        {exportNotice ? (
          <Alert severity="warning" onClose={() => setExportNotice(null)} sx={{ mb: 0 }}>
            {exportNotice}
          </Alert>
        ) : null}

        {!isInitialLoading && analytics ? (
          <Stack spacing={2.5}>
            <Card variant="outlined" sx={sectionCardSx}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" sx={{ flex: 1, ...sectionTitleSx }}>
                      Leitura principal
                    </Typography>
                    <AiBtn
                      cfg={{
                        metric: "survey_resultado_principal",
                        metricName: "Resultado principal da pesquisa",
                        metricLabel: metricTypeLabel,
                        metricDefinition:
                          "Interpretação da métrica principal da pesquisa considerando objetivo e contexto.",
                        metricValue: {
                          scoreSummary,
                          completionRate,
                          startedCount,
                          completedCount,
                        },
                        metricHuman: metricDisplayValue,
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {getPrimaryObjectiveText(metricType, metricMode)}
                  </Typography>
                  {primaryQuestionDisplayTitle ? (
                    <Typography variant="caption" color="text.secondary">
                      Pergunta principal: {primaryQuestionDisplayTitle}
                    </Typography>
                  ) : null}
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ md: "center" }}
                    sx={{ p: 1.5, borderRadius: 2, bgcolor: theme.palette.action.hover }}
                  >
                    <Box sx={{ minWidth: 190 }}>
                      <Typography variant="caption" color="text.secondary">
                        {metricMode === "distribution"
                          ? "Resposta principal"
                          : `Indicador principal (${metricTypeLabel})`}
                      </Typography>
                      <Typography variant="h2" sx={{ lineHeight: 1.1, fontSize: { xs: "2rem", md: metricMode === "distribution" ? "2rem" : "3.75rem" } }}>
                        {metricDisplayValue}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        {metricHelper}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={`Amostra: ${scoreSummary.sampleSize ?? 0}`} variant="outlined" />
                      {metricMode === "score" && metricType !== "custom" ? (
                        <Chip
                          label={`Favorabilidade: ${formatPercent(scoreSummary.favorability)}`}
                          variant="outlined"
                        />
                      ) : null}
                      {metricMode === "score" && metricTypeLabel === "NPS" ? (
                        <Chip label={npsBand.label} color={npsBand.color} sx={{ fontWeight: 600 }} />
                      ) : null}
                      {metricMode === "score" &&
                      metricTypeLabel !== "NPS" &&
                      Number(scoreSummary.sampleSize || 0) === 0 ? (
                        <Chip label="Sem dados suficientes" variant="outlined" />
                      ) : null}
                      {(metricMode === "distribution" ? scoreSummary.distribution || [] : [])
                        .slice(0, 3)
                        .map((item) => (
                        <Chip
                          key={item.label}
                          label={`${repairDisplayText(item.label)} · ${formatPercent(item.share)}`}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {metricMode === "score" && metricType === "nps" && npsBreakdown.length ? (
              <Card variant="outlined" sx={sectionCardSx}>
                <CardContent sx={{ p: 2.25 }}>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    Distribuição NPS
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Promotores (9–10), neutros (7–8) e detratores (0–6) na métrica principal.
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <Box sx={{ width: "100%", height: 240 }}>
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={npsBreakdown}
                              dataKey="value"
                              nameKey="name"
                              innerRadius={55}
                              outerRadius={90}
                              paddingAngle={2}
                            >
                              {npsBreakdown.map((entry, index) => (
                                <Cell key={entry.name} fill={NPS_COLORS[index % NPS_COLORS.length]} />
                              ))}
                            </Pie>
                            <RechartsTooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={`Promotores: ${scoreSummary.promoters || 0}`} color="success" variant="outlined" />
                        <Chip label={`Neutros: ${scoreSummary.passives || 0}`} color="warning" variant="outlined" />
                        <Chip label={`Detratores: ${scoreSummary.detractors || 0}`} color="error" variant="outlined" />
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ) : null}

            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <StatCard label="Sessões iniciadas" value={startedCount} helper="Pessoas que começaram a responder" />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatCard
                  label="Sessões concluídas"
                  value={completedCount}
                  helper="Pessoas que enviaram respostas"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatCard label="Taxa de conclusão" value={formatPercent(completionRate)} helper="Concluídas / iniciadas" />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatCard
                  label="Tempo médio de resposta"
                  value={formatDuration(avgCompletionSeconds)}
                  helper="Tempo até concluir"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} lg={8}>
                <Card variant="outlined" sx={{ height: "100%", ...sectionCardSx }}>
                  <CardContent sx={{ p: 2.25 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h6" sx={{ flex: 1, ...sectionTitleSx }}>
                        Evolução diária
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "survey_tendencia",
                          metricName: "Evolução diária de respostas",
                          metricLabel: "Tendência",
                          metricDefinition:
                            "Evolução diária de sessões iniciadas e concluídas para medir engajamento.",
                          metricValue: trend,
                          metricHuman: "",
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Veja se as pessoas estão respondendo e concluindo com consistência ao longo dos dias.
                    </Typography>
                    <Box sx={{ width: "100%", height: 320 }}>
                      {hasTrend ? (
                        <ResponsiveContainer>
                          <LineChart data={trend}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis allowDecimals={false} />
                            <RechartsTooltip />
                            <Legend />
                            <Line type="monotone" dataKey="startedCount" stroke="#1976d2" name="Iniciadas" />
                            <Line type="monotone" dataKey="completedCount" stroke="#2e7d32" name="Concluídas" />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <Stack sx={{ height: "100%" }} alignItems="center" justifyContent="center" spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            Ainda não há volume suficiente para exibir tendência.
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Card variant="outlined" sx={{ height: "100%", ...sectionCardSx }}>
                  <CardContent sx={{ p: 2.25 }}>
                    <Typography variant="h6" sx={sectionTitleSx}>
                      Funil de participação
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Mostra quantas pessoas começaram e quantas realmente terminaram.
                    </Typography>
                    <Stack spacing={1.25}>
                      <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "action.hover" }}>
                        <Typography variant="caption" color="text.secondary">
                          Iniciaram
                        </Typography>
                        <Typography variant="h5">{startedCount}</Typography>
                      </Box>
                      <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "action.hover" }}>
                        <Typography variant="caption" color="text.secondary">
                          Concluíram
                        </Typography>
                        <Typography variant="h5">{completedCount}</Typography>
                      </Box>
                      <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "action.hover" }}>
                        <Typography variant="caption" color="text.secondary">
                          Não concluíram
                        </Typography>
                        <Typography variant="h5">{dropoffCount}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card variant="outlined" sx={sectionCardSx}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h6" sx={{ flex: 1, ...sectionTitleSx }}>
                        Resultado por pergunta
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "survey_resultado_pergunta",
                          metricName: "Distribuição por pergunta",
                          metricLabel: "Pergunta",
                          metricDefinition:
                            "Distribuição de respostas da pergunta selecionada para identificar padrão dominante.",
                          metricValue: {
                            question: selectedQuestion?.title || "",
                            type: selectedQuestion?.type || "",
                            chartData,
                          },
                          metricHuman: selectedQuestion?.title || "",
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Selecione uma pergunta para ver exatamente quais respostas foram escolhidas.
                    </Typography>
                  </Box>
                  <FormControl sx={{ minWidth: { xs: "100%", md: 320 } }}>
                    <InputLabel>Pergunta</InputLabel>
                    <Select
                      label="Pergunta"
                      value={selectedQuestion?.questionId || ""}
                      onChange={(event) => setSelectedQuestionId(event.target.value)}
                    >
                      {questions.map((question) => (
                        <MenuItem key={question.questionId} value={question.questionId}>
                          {getQuestionMenuLabel(question)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                {selectedQuestion ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={8}>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                        <Chip label={getQuestionTypeLabel(selectedQuestion.type)} variant="outlined" />
                        {selectedQuestion.isPrimaryMetric ? (
                          <Chip label="Métrica principal" color="primary" variant="outlined" size="small" />
                        ) : null}
                        {selectedQuestion.scaleMax ? (
                          <Chip
                            label={`Escala ${selectedQuestion.scaleMin}-${selectedQuestion.scaleMax}`}
                            variant="outlined"
                          />
                        ) : null}
                      </Stack>
                      <Box sx={{ width: "100%", height: 280 }}>
                        {selectedQuestion?.type === "short_text" || selectedQuestion?.type === "long_text" ? (
                          selectedQuestionOpenTexts.length ? (
                            <Stack spacing={1} sx={{ height: "100%", overflowY: "auto", pr: 1 }}>
                              {selectedQuestionOpenTexts.slice(0, 50).map((item, index) => (
                                <Card key={`open-answer-${index}`} variant="outlined">
                                  <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
                                    <Typography variant="body2">{item.answerText}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatDateTime(item.answeredAt)}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          ) : (
                            <Stack sx={{ height: "100%" }} alignItems="center" justifyContent="center">
                              <Typography variant="body2" color="text.secondary">
                                Esta pergunta aberta ainda não recebeu respostas.
                              </Typography>
                            </Stack>
                          )
                        ) : chartData.length ? (
                          <ResponsiveContainer>
                            <BarChart data={chartData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis allowDecimals={false} />
                              <RechartsTooltip />
                              <Legend />
                              <Bar dataKey="respostas" fill="#1976d2" name="Respostas" />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <Stack sx={{ height: "100%" }} alignItems="center" justifyContent="center">
                            <Typography variant="body2" color="text.secondary">
                              Esta pergunta ainda não recebeu respostas.
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Resumo da pergunta
                          </Typography>
                          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Enunciado
                              </Typography>
                              <Typography variant="body2">
                                {repairDisplayText(selectedQuestion.title || "Pergunta sem título")}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {selectedQuestion?.type === "short_text" || selectedQuestion?.type === "long_text"
                                  ? "Volume de comentários"
                                  : "Opção mais escolhida"}
                              </Typography>
                              <Typography variant="body2">
                                {selectedQuestion?.type === "short_text" || selectedQuestion?.type === "long_text"
                                  ? `${selectedQuestionOpenTexts.length} comentário${
                                      selectedQuestionOpenTexts.length === 1 ? "" : "s"
                                    }`
                                  : topOption
                                  ? `${topOption.name} (${topOption.respostas} resposta${
                                      topOption.respostas === 1 ? "" : "s"
                                    })`
                                  : "Sem dados suficientes"}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography color="text.secondary">
                    Ainda não há perguntas com dados para exibir.
                  </Typography>
                )}
              </CardContent>
            </Card>

            {hasOpenTextQuestions ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={sectionCardSx}>
                  <CardContent sx={{ p: 2.25 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h6" sx={{ flex: 1, ...sectionTitleSx }}>
                        Temas mais citados
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "survey_temas",
                          metricName: "Temas mais citados",
                          metricLabel: "Temas",
                          metricDefinition: "Principais assuntos detectados em respostas abertas.",
                          metricValue: analytics.textInsights?.topThemes || [],
                          metricHuman: "",
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Palavras e temas que mais aparecem nos comentários.
                    </Typography>
                    <Stack spacing={1}>
                      {(analytics.textInsights?.topThemes || []).length ? (
                        analytics.textInsights.topThemes.map((theme) => (
                          <Stack key={theme.label} direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">{theme.label}</Typography>
                            <Chip label={theme.count} size="small" />
                          </Stack>
                        ))
                      ) : (
                        <Typography color="text.secondary">
                          Ainda não há temas suficientes nas respostas abertas.
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={sectionCardSx}>
                  <CardContent sx={{ p: 2.25 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h6" sx={{ flex: 1, ...sectionTitleSx }}>
                        Comentários recentes
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "survey_comentarios",
                          metricName: "Comentários recentes",
                          metricLabel: "Verbatims",
                          metricDefinition:
                            "Comentários textuais recentes para identificar elogios, dores e oportunidades.",
                          metricValue: (analytics.openTextResponses || []).slice(0, 20),
                          metricHuman: `${totalOpenTexts} comentários`,
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Últimos comentários recebidos ({totalOpenTexts} no total).
                    </Typography>
                    <Stack spacing={1} sx={{ maxHeight: 260, overflowY: "auto" }}>
                      {(analytics.openTextResponses || []).length ? (
                        analytics.openTextResponses.slice(0, 8).map((item, index) => (
                          <Box
                            key={`${item.answeredAt}-${index}`}
                            sx={{ pb: 1, px: 1.25, py: 1, borderRadius: 1.5, bgcolor: "action.hover" }}
                          >
                            <Typography variant="subtitle2">{repairDisplayText(item.questionTitle)}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {repairDisplayText(item.answerText)}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography color="text.secondary">Sem respostas abertas registradas.</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            ) : null}

            <Card variant="outlined" sx={sectionCardSx}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Typography variant="h6" sx={{ flex: 1, ...sectionTitleSx }}>
                    Participações recentes
                  </Typography>
                  <AiBtn
                    cfg={{
                      metric: "survey_sessoes",
                      metricName: "Participações recentes",
                      metricLabel: "Sessões",
                      metricDefinition:
                        "Participações recentes com canal, tempo e respostas detalhadas por pessoa.",
                      metricValue: sessions,
                      metricHuman: `${sessionsTotal.toLocaleString("pt-BR")} participações`,
                    }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Expanda uma linha para ver o enunciado de cada pergunta e a resposta escolhida. Carregando{" "}
                  {SESSIONS_PAGE_SIZE} participações por página
                  {sessionsTotal ? ` (${sessionsTotal.toLocaleString("pt-BR")} no total)` : ""}.
                </Typography>
                {fetchingSessions && !loadingSessions ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                    Atualizando lista…
                  </Typography>
                ) : null}

                <Box
                  sx={{
                    ...sessionSummaryGridSx,
                    px: 2,
                    py: 0.75,
                    mb: 0.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Data
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Cliente
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Protocolo
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Origem
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Status
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Tempo
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Respostas
                  </Typography>
                </Box>

                <Stack spacing={1}>
                  {loadingSessions && !sessions.length ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Carregando participações…
                    </Typography>
                  ) : null}
                  {!loadingSessions && !sessions.length ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Nenhuma participação encontrada para os filtros selecionados.
                    </Typography>
                  ) : null}
                  {sessions.map((session) => {
                    const answers = session.answers || [];
                    const answerCount = session.answerCount ?? answers.length;
                    const protocol = formatProtocolLabel(session.leadId);
                    const clientLabel = repairDisplayText(session.clientName) || "";
                    return (
                      <Accordion
                        key={`session-${session.id}`}
                        disableGutters
                        elevation={0}
                        variant="outlined"
                        sx={{ borderRadius: 1.5, overflow: "hidden", "&:before": { display: "none" } }}
                      >
                        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: { xs: 1.5, lg: 2 } }}>
                          <Box sx={{ display: { xs: "block", lg: "none" }, width: "100%", pr: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {clientLabel || session.responseToken || "—"}
                              </Typography>
                              {protocol ? (
                                <Chip
                                  size="small"
                                  component={RouterLink}
                                  to={`/chat/${protocol}`}
                                  clickable
                                  label={`#${protocol}`}
                                  variant="outlined"
                                  onClick={(event) => event.stopPropagation()}
                                />
                              ) : null}
                              <Chip
                                size="small"
                                label={SESSION_STATUS_LABELS[session.status] || session.status}
                                color={session.status === "completed" ? "success" : "default"}
                                variant="outlined"
                              />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                              {formatDateTime(session.startedAt)} · {getSessionSourceLabel(session)} ·{" "}
                              {formatDuration(getSessionDurationSeconds(session))} ·{" "}
                              {formatResponseCount(answerCount)}
                            </Typography>
                          </Box>

                          <Box sx={sessionSummaryGridSx}>
                            <Typography variant="body2" noWrap>
                              {formatDateTime(session.startedAt)}
                            </Typography>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                              {clientLabel || "—"}
                            </Typography>
                            <Box>
                              {protocol ? (
                                <Typography
                                  component={RouterLink}
                                  to={`/chat/${protocol}`}
                                  variant="body2"
                                  noWrap
                                  onClick={(event) => event.stopPropagation()}
                                  sx={{
                                    fontWeight: 600,
                                    color: "primary.main",
                                    textDecoration: "none",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  #{protocol}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="body2" noWrap color="text.secondary">
                              {getSessionSourceLabel(session)}
                            </Typography>
                            <Box>
                              <Chip
                                size="small"
                                label={SESSION_STATUS_LABELS[session.status] || session.status}
                                color={session.status === "completed" ? "success" : "default"}
                                variant="outlined"
                              />
                            </Box>
                            <Typography variant="body2" noWrap>
                              {formatDuration(getSessionDurationSeconds(session))}
                            </Typography>
                            <Typography variant="body2" noWrap>
                              {formatResponseCount(answerCount)}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, px: { xs: 1.5, lg: 2 }, pb: 2 }}>
                          {answers.length ? (
                            <Stack spacing={1.25}>
                              {answers.map((answer, index) => {
                                const answerText = formatAnswer(answer);
                                const isEmpty = !answerText;
                                return (
                                  <Box
                                    key={`ans-${session.id}-${answer.questionId || index}`}
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 1.5,
                                      border: "1px solid",
                                      borderColor: "divider",
                                      bgcolor: "background.default",
                                    }}
                                  >
                                    <Stack
                                      direction={{ xs: "column", sm: "row" }}
                                      spacing={{ xs: 0.75, sm: 2 }}
                                      alignItems={{ sm: "flex-start" }}
                                    >
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="flex-start"
                                        sx={{ flex: 1, minWidth: 0 }}
                                      >
                                        {answer.questionPosition ? (
                                          <Chip
                                            size="small"
                                            label={answer.questionPosition}
                                            sx={{ minWidth: 28, fontWeight: 700 }}
                                          />
                                        ) : null}
                                        <Box sx={{ minWidth: 0 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {getAnswerQuestionLabel(answer)}
                                          </Typography>
                                          {getAnswerQuestionHint(answer) ? (
                                            <Typography variant="caption" color="text.secondary">
                                              {getAnswerQuestionHint(answer)}
                                            </Typography>
                                          ) : (
                                            <Typography variant="caption" color="text.secondary">
                                              {getQuestionTypeLabel(answer.questionType)}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Stack>
                                      <Box sx={{ minWidth: { sm: 160 }, pl: { sm: 0, xs: answer.questionPosition ? 5.5 : 0 } }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                          Resposta
                                        </Typography>
                                        <Typography
                                          variant="body1"
                                          sx={{
                                            fontWeight: isEmpty ? 400 : 600,
                                            fontStyle: isEmpty ? "italic" : "normal",
                                            color: isEmpty ? "text.secondary" : "text.primary",
                                          }}
                                        >
                                          {isEmpty ? "Não respondeu" : answerText}
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  </Box>
                                );
                              })}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {session.status === "completed"
                                ? "Sessão concluída sem respostas registradas."
                                : "Esta sessão ainda não possui respostas detalhadas."}
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>

                {sessionsPagination.totalPages > 1 ? (
                  <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                    <Pagination
                      count={sessionsPagination.totalPages}
                      page={sessionsPage}
                      onChange={(_event, page) => setSessionsPage(page)}
                      color="primary"
                      shape="rounded"
                      disabled={fetchingSessions}
                    />
                  </Stack>
                ) : null}
              </CardContent>
            </Card>
          </Stack>
        ) : null}
      </Stack>
      <MetricAiInsightsDialog
        open={metricDialogOpen}
        metric={metricDialogMetric}
        metricName={metricDialogName}
        metricLabel={metricDialogLabel}
        metricHuman={metricDialogHuman}
        period={metricDialogBasePayload?.period || null}
        loading={metricInsightsMutation.isLoading}
        error={metricDialogError}
        data={metricDialogData}
        scope="surveys"
        basePayload={metricDialogBasePayload}
        onClose={() => setMetricDialogOpen(false)}
      />
    </Container>
  );
}
